import { useEffect, useState } from 'react';

// How often to poll /api/version. 5 minutes balances "users see deploys
// promptly" against "we don't hammer the API for what's effectively a
// human-cadence event." Deploys are manual + rare.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

// Once a mismatch is detected, we don't keep polling — the banner takes
// over and either auto-reloads or the user clicks "Reload now."
type State = { stale: boolean; serverVersion: string | null };

export const useVersionCheck = (): State => {
  const [state, setState] = useState<State>({ stale: false, serverVersion: null });

  useEffect(() => {
    // In dev, the bundled version is 'dev' and the API returns 'dev' too —
    // they'll match. Skip entirely to avoid noise.
    if (__APP_VERSION__ === 'dev') return;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch('/api/version', { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = (await res.json()) as { version: string };
        if (cancelled) return;
        if (version && version !== __APP_VERSION__) {
          setState({ stale: true, serverVersion: version });
        }
      } catch {
        // Network blip — try again next interval.
      }
    };

    void check();
    const id = window.setInterval(check, POLL_INTERVAL_MS);

    // Also re-check when the tab regains focus — users often leave OneDesk
    // open in a background tab; this catches stale sessions immediately when
    // they return to it.
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return state;
};
